/**
 * useCloudStorage — 云存储 OAuth + 文件管理钩子
 *
 * Architecture 3.0 Phase 4: 从 AppLayout.tsx 抽离
 * 封装云存储的连接/断开/文件列表/文件夹导航逻辑
 */

import { useState, useCallback } from 'react';
import { API_BASE_URL } from '../services/api';

// ─── Types ─────────────────────────────────

export interface CloudProvider {
  source: string;
  displayName: string;
  connected: boolean;
}

export interface CloudFileEntry {
  id: string;
  name: string;
  parentName: string;
  isFolder?: boolean;
}

export interface CloudFilesState {
  source: string;
  files: CloudFileEntry[];
  error?: string;
  currentFolderId?: string;
  pathHistory?: { id: string; name: string }[];
  isLoading?: boolean;
}

// ─── Hook ──────────────────────────────────

export function useCloudStorage() {
  const [cloudProviders, setCloudProviders] = useState<CloudProvider[]>([]);
  const [cloudFiles, setCloudFiles] = useState<CloudFilesState[]>([]);

  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  }), []);

  // ─── 加载文件列表 ──────────────────────────
  const loadCloudFiles = useCallback(async (source: string) => {
    setCloudFiles(prev => {
      const ext = prev.find(cf => cf.source === source);
      const filtered = prev.filter(cf => cf.source !== source);
      return [...filtered, { ...(ext || {}), source, isLoading: true, files: ext?.files || [] }];
    });
    try {
      const resp = await fetch(`${API_BASE_URL}/api/cloud/${source}/files`, {
        headers: getAuthHeaders(),
      });
      const data = await resp.json();
      if (!resp.ok || data.error) {
        const errMsg = data.error || `HTTP ${resp.status}`;
        console.error(`[Cloud] ${source} files failed:`, errMsg);
        setCloudFiles(prev => {
          const filtered = prev.filter(cf => cf.source !== source);
          return [...filtered, { source, files: [], error: errMsg, isLoading: false }];
        });
        return;
      }
      setCloudFiles(prev => {
        const filtered = prev.filter(cf => cf.source !== source);
        return [...filtered, { source, files: data.files || [], isLoading: false }];
      });
    } catch (err: any) {
      console.error(`[Cloud] ${source} loadFiles exception:`, err);
      setCloudFiles(prev => {
        const filtered = prev.filter(cf => cf.source !== source);
        return [...filtered, { source, files: [], error: err?.message || 'Network error' }];
      });
    }
  }, [getAuthHeaders]);

  // ─── OAuth 连接 ────────────────────────────
  const handleCloudConnect = useCallback(async (source: string) => {
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${API_BASE_URL}/api/cloud/${source}/auth`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Origin': window.location.origin },
      });
      const data = await resp.json();
      if (data.authUrl) {
        const popup = window.open(data.authUrl, `connect-${source}`, 'width=600,height=700,popup=yes');
        const listener = async (event: MessageEvent) => {
          if (event.data?.type === 'cloud-oauth-callback' && event.data?.source === source) {
            window.removeEventListener('message', listener);
            popup?.close();
            const cbResp = await fetch(`${API_BASE_URL}/api/cloud/${source}/callback`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ code: event.data.code, redirectUri: `${window.location.origin}/app/cloud-callback` }),
            });
            if (cbResp.ok) {
              setCloudProviders(prev => prev.map(p => p.source === source ? { ...p, connected: true } : p));
              loadCloudFiles(source);
            }
          }
        };
        window.addEventListener('message', listener);
      }
    } catch (err) {
      console.error('[Cloud] Connect failed:', err);
    }
  }, [loadCloudFiles]);

  // ─── 断开连接 ──────────────────────────────
  const handleCloudDisconnect = useCallback(async (source: string) => {
    const token = localStorage.getItem('auth_token');
    await fetch(`${API_BASE_URL}/api/cloud/${source}/disconnect`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` },
    });
    setCloudProviders(prev => prev.map(p => p.source === source ? { ...p, connected: false } : p));
    setCloudFiles(prev => prev.filter(cf => cf.source !== source));
  }, []);

  // ─── 文件夹导航 ────────────────────────────
  const handleCloudFolderClick = useCallback(async (source: string, folderId: string, folderName: string) => {
    setCloudFiles(prev => prev.map(cf => cf.source === source ? { ...cf, isLoading: true } : cf));
    try {
      const token = localStorage.getItem('auth_token');
      const resp = await fetch(`${API_BASE_URL}/api/cloud/${source}/folder/${folderId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await resp.json();
      if (resp.ok && data.items) {
        setCloudFiles(prev => prev.map(cf => {
          if (cf.source === source) {
            const newHistory = [...(cf.pathHistory || []), { id: folderId, name: folderName }];
            return { ...cf, files: data.items, currentFolderId: folderId, pathHistory: newHistory, isLoading: false };
          }
          return cf;
        }));
      } else {
        setCloudFiles(prev => prev.map(cf => cf.source === source ? { ...cf, isLoading: false } : cf));
      }
    } catch (err) {
      console.error(`[Cloud] ${source} folder ${folderId} failed:`, err);
    }
  }, []);

  // ─── 文件夹返回 ────────────────────────────
  const handleCloudFolderBack = useCallback(async (source: string) => {
    setCloudFiles(prev => {
      const cf = prev.find(p => p.source === source);
      if (!cf) return prev;
      const history = cf.pathHistory || [];

      if (history.length <= 1) {
        loadCloudFiles(source);
        return prev.map(p => p.source === source ? { ...p, currentFolderId: undefined, pathHistory: [], isLoading: true } : p);
      }

      const newHistory = [...history];
      newHistory.pop();
      const parentFolder = newHistory[newHistory.length - 1];

      const loadParent = async () => {
        try {
          const token = localStorage.getItem('auth_token');
          const resp = await fetch(`${API_BASE_URL}/api/cloud/${source}/folder/${parentFolder.id}`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          const data = await resp.json();
          if (resp.ok && data.items) {
            setCloudFiles(curr => curr.map(p => p.source === source ? { ...p, files: data.items, currentFolderId: parentFolder.id, pathHistory: newHistory, isLoading: false } : p));
          } else {
            setCloudFiles(curr => curr.map(p => p.source === source ? { ...p, isLoading: false } : p));
          }
        } catch (err) {
          console.error('[Cloud] Load parent error:', err);
          setCloudFiles(curr => curr.map(p => p.source === source ? { ...p, isLoading: false } : p));
        }
      };
      loadParent();

      return prev.map(p => p.source === source ? { ...p, currentFolderId: parentFolder.id, pathHistory: newHistory, isLoading: true } : p);
    });
  }, [loadCloudFiles]);

  // ─── 初始化加载 providers ─────────────────
  const initCloudProviders = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE_URL}/api/cloud/providers`, {
        headers: getAuthHeaders(),
      });
      const data = await resp.json();
      if (data.providers) {
        setCloudProviders(data.providers);
        data.providers.filter((p: any) => p.connected).forEach((p: any) => {
          loadCloudFiles(p.source);
        });
      }
    } catch {
      // silent fail
    }
  }, [getAuthHeaders, loadCloudFiles]);

  return {
    cloudProviders,
    setCloudProviders,
    cloudFiles,
    handleCloudConnect,
    handleCloudDisconnect,
    loadCloudFiles,
    handleCloudFolderClick,
    handleCloudFolderBack,
    initCloudProviders,
  };
}
